import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Trophy, Snowflake } from "lucide-react";

/**
 * StreakLeaderboard — Top-N users by two-way accountability streak. Only shown
 * on the Community weekly-recap feed to nudge friendly competition.
 */
export default function StreakLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/community/streak-leaderboard", { params: { limit: 5 } })
      .then(({ data }) => { if (alive) setRows(data?.leaderboard || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (rows.length === 0) return null;

  const medal = (i) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`);

  return (
    <div
      className="border border-slate-200 bg-white p-4 mb-4"
      data-testid="streak-leaderboard"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] flex items-center gap-1.5">
            <Trophy className="w-3 h-3" /> // Top streaks · This month
          </div>
          <div className="font-display font-black uppercase tracking-tight text-lg text-slate-900 dark:text-white leading-none mt-1">
            Leaderboard
          </div>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
          Two-way streak
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {rows.map((r, i) => (
          <div
            key={r.user_id}
            className={[
              "border p-3 flex flex-col items-center text-center",
              r.is_me
                ? "border-[#0D9488] bg-[#0D9488]/10"
                : "border-slate-200 bg-slate-50",
            ].join(" ")}
            data-testid={`streak-leaderboard-row-${i}`}
          >
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              {medal(i)}
            </div>
            {r.avatar_url ? (
              <img
                src={r.avatar_url}
                alt={r.name}
                className="w-8 h-8 rounded-full border border-slate-200 mb-1"
              />
            ) : (
              <div className="w-8 h-8 rounded-full border border-slate-200 bg-slate-50 mb-1" />
            )}
            <div className="text-xs text-slate-900 dark:text-white truncate w-full">
              {r.is_me ? "You" : r.name}
            </div>
            <div className="font-display font-black text-2xl leading-none text-[#0D9488] tabular-nums mt-1">
              {r.streak}
            </div>
            <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">
              wk streak
            </div>
            {r.frozen_weeks > 0 && (
              <div className="mt-1 inline-flex items-center gap-0.5 font-mono-num text-[9px] uppercase tracking-widest text-cyan-400">
                <Snowflake className="w-2.5 h-2.5" /> {r.frozen_weeks} frozen
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
